import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  addWorkspaceMember,
  getListWorkspaceMembersQueryKey,
  getListWorkspacePeopleQueryKey,
  getReadWorkspaceAccessLogQueryKey,
  removeWorkspaceMember,
  updateWorkspace,
  updateWorkspaceMember,
  useListWorkspaceMembers,
} from '@/lib/api/generated/api';
import type {
  MemberRemoval,
  WorkspaceRole,
  WorkspaceVisibility,
} from '@/lib/api/generated/model';
import { workspaceKeys } from '@/features/workspaces';

/**
 * What a refusal means, in words (spec 186). The service answers with a code;
 * the person needs the sentence that tells them what to do next.
 */
const REFUSALS: Record<string, string> = {
  LAST_OWNER: 'A workspace needs an owner. Make someone else an owner first.',
  MEMBER_ALREADY_EXISTS: 'This person is already in the workspace.',
  MEMBER_NOT_IN_ORGANIZATION:
    'This person is not an organization member and cannot be added.',
  MEMBER_VERIFICATION_FAILED:
    'Organization membership could not be verified. Try again in a moment.',
  MEMBER_NOT_FOUND: 'This person is no longer in the workspace.',
  PERSON_NOT_FOUND: 'This person could not be found.',
  WORKSPACE_OWNERS_ONLY: 'Only this workspace’s owners can do this.',
};

/** The sentence for a failed response, or the fallback when its code is unknown. */
export function refusalMessage(data: unknown, fallback: string): string {
  const code =
    data !== null && typeof data === 'object' && 'error' in data
      ? String(data.error)
      : '';
  return REFUSALS[code] ?? fallback;
}

function check(
  response: { status: number; data: unknown },
  expected: number,
  fallback: string,
): void {
  if (response.status !== expected) {
    throw new Error(refusalMessage(response.data, fallback));
  }
}

/** The workspace's members, its visibility and its outside followers. */
export function useWorkspaceMembers(workspaceId: string, enabled = true) {
  const query = useListWorkspaceMembers(workspaceId, {
    query: { enabled },
  });
  const response = query.data;
  return {
    data: response?.status === 200 ? response.data : undefined,
    isPending: query.isPending,
    isError:
      query.isError || (response !== undefined && response.status !== 200),
    refetch: query.refetch,
  };
}

/** After any change: the members, the pickers, the workspace itself and its log. */
function useRefresh() {
  const queryClient = useQueryClient();
  return (workspaceId: string) => {
    void queryClient.invalidateQueries({
      queryKey: getReadWorkspaceAccessLogQueryKey(workspaceId),
    });
    void queryClient.invalidateQueries({
      queryKey: getListWorkspaceMembersQueryKey(workspaceId),
    });
    void queryClient.invalidateQueries({
      queryKey: getListWorkspacePeopleQueryKey(workspaceId),
    });
    void queryClient.invalidateQueries({
      queryKey: workspaceKeys.detail(workspaceId),
    });
    void queryClient.invalidateQueries({ queryKey: workspaceKeys.list() });
  };
}

export function useAddMember() {
  const refresh = useRefresh();
  return useMutation<
    void,
    Error,
    { workspaceId: string; userId: string; role?: WorkspaceRole }
  >({
    mutationFn: async ({ workspaceId, userId, role }) => {
      check(
        await addWorkspaceMember(workspaceId, {
          user_id: userId,
          role: role ?? 'member',
        }),
        201,
        'This person could not be added.',
      );
    },
    onSuccess: (_result, { workspaceId }) => {
      refresh(workspaceId);
    },
  });
}

export function useChangeMemberRole() {
  const refresh = useRefresh();
  return useMutation<
    void,
    Error,
    { workspaceId: string; userId: string; role: WorkspaceRole }
  >({
    mutationFn: async ({ workspaceId, userId, role }) => {
      check(
        await updateWorkspaceMember(workspaceId, userId, { role }),
        200,
        'The role could not be changed.',
      );
    },
    onSuccess: (_result, { workspaceId }) => {
      refresh(workspaceId);
    },
  });
}

/** Remove someone, or leave when it is yourself. */
export function useRemoveMember() {
  const refresh = useRefresh();
  return useMutation<
    MemberRemoval,
    Error,
    { workspaceId: string; userId: string }
  >({
    mutationFn: async ({ workspaceId, userId }) => {
      const response = await removeWorkspaceMember(workspaceId, userId);
      check(response, 200, 'This person could not be removed.');
      return response.data as MemberRemoval;
    },
    onSuccess: (_result, { workspaceId }) => {
      refresh(workspaceId);
    },
  });
}

export function useUpdateWorkspaceSettings() {
  const refresh = useRefresh();
  return useMutation<
    void,
    Error,
    {
      workspaceId: string;
      name?: string;
      visibility?: WorkspaceVisibility;
    }
  >({
    mutationFn: async ({ workspaceId, name, visibility }) => {
      check(
        await updateWorkspace(workspaceId, {
          ...(name === undefined ? {} : { name }),
          ...(visibility === undefined ? {} : { visibility }),
        }),
        200,
        'The workspace could not be updated.',
      );
    },
    onSuccess: (_result, { workspaceId }) => {
      refresh(workspaceId);
    },
  });
}
